import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { fmtDate } from "../lib/format";
import { printTable } from "../lib/printView";
import type { Project, DrawingSetIssue } from "../lib/types";

/* Compare two set issues — which sheets were added, withdrawn, revised or left
   alone between (say) Issue C and Issue D. The delta a contractor actually
   needs when a new set lands on site. */

type Change = "added" | "withdrawn" | "revised" | "unchanged";
const HUE: Record<Change, string> = { added: "var(--ok)", withdrawn: "var(--danger)", revised: "var(--accent)", unchanged: "var(--ink-3)" };
const LABEL: Record<Change, string> = { added: "Added", withdrawn: "Withdrawn", revised: "Revised", unchanged: "Unchanged" };

interface Row { key: string; code: string; title: string; building?: string; from?: string; to?: string; change: Change }

function diff(a: DrawingSetIssue, b: DrawingSetIssue): Row[] {
  const keyOf = (s: any) => `${s.building || ""}|${(s.code || "").trim().toUpperCase()}`;
  const from = new Map(a.sheets.map((s) => [keyOf(s), s]));
  const to = new Map(b.sheets.map((s) => [keyOf(s), s]));
  const rows: Row[] = [];
  for (const [k, s] of to) {
    const prev = from.get(k);
    if (!prev) rows.push({ key: k, code: s.code, title: s.title, building: s.building, to: s.revision, change: "added" });
    else if ((prev.revision || "") !== (s.revision || "")) rows.push({ key: k, code: s.code, title: s.title, building: s.building, from: prev.revision, to: s.revision, change: "revised" });
    else rows.push({ key: k, code: s.code, title: s.title, building: s.building, from: prev.revision, to: s.revision, change: "unchanged" });
  }
  for (const [k, s] of from) if (!to.has(k)) rows.push({ key: k, code: s.code, title: s.title, building: s.building, from: s.revision, change: "withdrawn" });
  const order: Change[] = ["revised", "added", "withdrawn", "unchanged"];
  return rows.sort((x, y) => order.indexOf(x.change) - order.indexOf(y.change) || x.code.localeCompare(y.code, undefined, { numeric: true }));
}

export function SetDiffModal({ p, onClose }: { p: Project; onClose: () => void }) {
  const sets = [...(p.drawingSets ?? [])].sort((a, b) => +new Date(a.issuedOn) - +new Date(b.issuedOn));
  const [aId, setAId] = useState(sets[sets.length - 2]?.id ?? sets[0]?.id ?? "");
  const [bId, setBId] = useState(sets[sets.length - 1]?.id ?? "");
  const [showAll, setShowAll] = useState(false);
  const a = sets.find((s) => s.id === aId), b = sets.find((s) => s.id === bId);
  const rows = a && b ? diff(a, b) : [];
  const counts = rows.reduce((m, r) => { m[r.change] = (m[r.change] ?? 0) + 1; return m; }, {} as Record<Change, number>);
  const shown = showAll ? rows : rows.filter((r) => r.change !== "unchanged");

  return (
    <Modal open onClose={onClose} title="Compare set issues" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          What changed between two issues of the set — the schedule to send with a reissue so nobody works off a superseded sheet.
        </p>
        {sets.length < 2 ? (
          <div className="panel" style={{ padding: 22, textAlign: "center", color: "var(--ink-3)" }}>You need at least two set issues to compare. Issue a set revision first.</div>
        ) : (
          <>
            <div className="row wrap" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1, minWidth: 200 }}><label>From</label>
                <select className="select" value={aId} onChange={(e) => setAId(e.target.value)}>{sets.map((s) => <option key={s.id} value={s.id}>{s.label} · {fmtDate(s.issuedOn)}</option>)}</select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 200 }}><label>To</label>
                <select className="select" value={bId} onChange={(e) => setBId(e.target.value)}>{sets.map((s) => <option key={s.id} value={s.id}>{s.label} · {fmtDate(s.issuedOn)}</option>)}</select>
              </div>
            </div>
            {a && b && a.id === b.id && <div className="dim" style={{ fontSize: 12.5 }}>Pick two different issues.</div>}
            {a && b && a.id !== b.id && (
              <>
                <div className="row wrap between" style={{ gap: 10 }}>
                  <span className="row wrap" style={{ gap: 6 }}>
                    {(["revised", "added", "withdrawn", "unchanged"] as Change[]).map((c) => counts[c] ? <span key={c} className="badge" style={{ fontSize: 9.5, color: HUE[c] }}>{counts[c]} {LABEL[c].toLowerCase()}</span> : null)}
                  </span>
                  <span className="row" style={{ gap: 8 }}>
                    <button className="chip" style={{ fontSize: 11 }} onClick={() => setShowAll((v) => !v)}>{showAll ? "Changes only" : "Show unchanged"}</button>
                    <button className="btn ghost sm" onClick={() => printTable({
                      title: "Drawing issue comparison", subtitle: `${p.name} · ${p.code}`,
                      meta: [`From: ${a.label} (${fmtDate(a.issuedOn)})`, `To: ${b.label} (${fmtDate(b.issuedOn)})`],
                      columns: [{ key: "code", label: "Sheet", width: "70px" }, { key: "title", label: "Title" }, { key: "building", label: "Building" }, { key: "from", label: "From", width: "50px", align: "center" }, { key: "to", label: "To", width: "50px", align: "center" }, { key: "change", label: "Change", width: "70px" }],
                      rows: shown.map((r) => ({ code: r.code, title: r.title, building: r.building ?? "", from: r.from ?? "—", to: r.to ?? "—", change: LABEL[r.change] })),
                      note: "Withdrawn sheets are no longer part of the issued set and should be destroyed or marked superseded.",
                    })}><Icon.report width={13} />Print</button>
                  </span>
                </div>
                <div className="col" style={{ gap: 2, maxHeight: "50vh", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 10, padding: 6 }}>
                  {shown.length === 0 && <div className="dim" style={{ padding: 20, textAlign: "center", fontSize: 13 }}>Nothing changed between these two issues.</div>}
                  {shown.map((r) => (
                    <div key={r.key} className="row between wrap" style={{ padding: "8px 10px", borderRadius: 8, gap: 10, background: r.change === "unchanged" ? "transparent" : "color-mix(in srgb, var(--paper-2) 70%, transparent)" }}>
                      <span className="row" style={{ gap: 9, minWidth: 0 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 7, background: HUE[r.change], flexShrink: 0 }} />
                        <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{r.code}</span>
                        <span style={{ fontSize: 12.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
                        {r.building && <span className="badge" style={{ fontSize: 8.5 }}>{r.building}</span>}
                      </span>
                      <span className="row" style={{ gap: 8, flexShrink: 0 }}>
                        <span className="mono dim" style={{ fontSize: 11 }}>{r.from ?? "—"} → {r.to ?? "—"}</span>
                        <span className="badge" style={{ fontSize: 8.5, color: HUE[r.change] }}>{LABEL[r.change]}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
